import React, { useState, useEffect } from 'react';
import { FiX, FiStar } from 'react-icons/fi';
import { saveFormFeedback } from '../utils/formFeedback';

const FeedbackModal = ({ onClose, userEmail }) => {
  const [feedback, setFeedback] = useState({
    reason: '',
    feedbackText: '',
    rating: 0,
    email: userEmail || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showInstantResponse, setShowInstantResponse] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const reasons = [
    { 
      value: 'ليس لدي حساب nsave', 
      response: 'قم بإنشاء حسابك الآن من خلال رابط الإحالة الخاص بنا! لا تنتظر قبول التوثيق، فقط أنشئ الحساب وقدم طلبك مباشرة.',
      link: 'https://web.nsave.com/referral/egyptianfreelancers',
      linkText: 'إنشاء حساب الآن ←'
    },
    { 
      value: 'انتظر توثيق حسابي', 
      response: 'لا يشترط قبول التوثيق قبل التسجيل في الهاكاثون! المهم أن تكون قد قدمت طلب التوثيق فقط. يمكنك التقديم الآن والمتابعة!'
    },
    { 
      value: 'ليس لدي فريق/فكرة', 
      response: 'لا تقلق! يمكنك التسجيل بمفردك وبناء فريقك لاحقاً، أو انضم لسيرفر Discord الخاص بنا للبحث عن فريق والتواصل مع المشاركين الآخرين!',
      link: 'https://discord.gg/jpRGNsyfcw',
      linkText: 'انضم للسيرفر الآن ←'
    },
    { value: 'النموذج طويل جداً', response: null },
    { value: 'معلومات غير واضحة', response: null },
    { value: 'مشاكل تقنية', response: null },
    { value: 'غيرت رأيي', response: null },
  ];

  const selectedReason = reasons.find(r => r.value === feedback.reason);
  const instantResponse = selectedReason?.response;
  const responseLink = selectedReason?.link;
  const responseLinkText = selectedReason?.linkText || 'اذهب الآن ←';

  // Animate in the instant response modal
  useEffect(() => {
    if (showInstantResponse) {
      // Trigger animation after a small delay
      setTimeout(() => setIsAnimatingIn(true), 10);
    } else {
      setIsAnimatingIn(false);
      setIsAnimatingOut(false);
    }
  }, [showInstantResponse]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const result = await saveFormFeedback(feedback);
    
    if (result.success) {
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } else {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleReasonChange = (value) => {
    setFeedback({ ...feedback, reason: value });
    const selected = reasons.find(r => r.value === value);
    if (selected?.response) {
      setShowInstantResponse(true);
    }
  };

  const closeInstantResponse = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setShowInstantResponse(false);
    }, 300); // Match the transition duration
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
        <div className="bg-dark-blue border-2 border-yellow rounded-xl sm:rounded-2xl p-5 sm:p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border-2 sm:border-4 border-green-500">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-yellow mb-2">شكراً لك!</h3>
          <p className="text-sm sm:text-base text-white/80">ملاحظاتك ستساعدنا على التحسين</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-hidden bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-fadeIn">
      <div className="bg-dark-blue overflow-hidden border-2 border-yellow rounded-xl sm:rounded-2xl max-w-3xl w-[96%] sm:w-full max-h-[90vh] sm:max-h-[95vh] overflow-y-auto animate-slideUp">
        <div className="sticky top-0 bg-yellow px-3 py-2.5 sm:px-6 sm:py-4 flex justify-between items-center">
          <h3 className="text-base sm:text-xl font-bold text-[#05339c]">قبل أن تغادر...</h3>
          <button
            onClick={handleSkip}
            className="text-[#05339c] hover:bg-[#05339c] hover:text-yellow p-1.5 sm:p-2 rounded-lg transition-all"
          >
            <FiX size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-3 sm:p-6 space-y-3 sm:space-y-6 overflow-y-auto">
          <div>
            <p className="text-white text-xs sm:text-sm mb-3 sm:mb-4 text-center">
              نحب أن نعرف رأيك لتحسين تجربتك
            </p>
          </div>

          {/* Reason Selection */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-yellow mb-2 sm:mb-3">
              لماذا لم تكمل التسجيل؟
            </label>
            <div className="space-y-1.5 sm:space-y-2">
              {reasons.map((reason) => (
                <label
                  key={reason.value}
                  className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-blue/20 rounded-lg cursor-pointer hover:bg-blue/30 transition-colors border-2 border-transparent hover:border-yellow"
                >
                  <input
dir="ltr"                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={feedback.reason === reason.value}
                    onChange={(e) => handleReasonChange(e.target.value)}
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 appearance-none rounded-full border-2 border-yellow bg-transparent cursor-pointer checked:bg-yellow checked:border-yellow relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 sm:before:w-2 sm:before:h-2 before:rounded-full before:bg-dark-blue checked:before:block before:hidden transition-all duration-200"
                  />
                  <span className="text-white text-xs sm:text-sm">{reason.value}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Feedback */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-yellow mb-1.5 sm:mb-2">
              هل تود إضافة أي ملاحظات؟ (اختياري)
            </label>
            <textarea
              value={feedback.feedbackText}
              onChange={(e) => setFeedback({ ...feedback, feedbackText: e.target.value })}
              rows="3"
              className="w-full px-2.5 py-2 sm:px-3 sm:py-2 bg-blue/20 border-2 border-yellow rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow text-white text-xs sm:text-sm placeholder-white/50"
              placeholder="اكتب ملاحظاتك هنا..."
            />
          </div>

          {/* Rating */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-yellow mb-2 sm:mb-3 text-center">
              كيف تقيم الموقع؟
            </label>
            <div className="flex justify-center gap-1 sm:gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFeedback({ ...feedback, rating: star })}
                  className="transition-all duration-200 hover:scale-110"
                >
                  <FiStar
                    size={24}
                    className={`w-6 h-6 sm:w-8 sm:h-8 ${
                      star <= feedback.rating
                        ? 'fill-yellow text-yellow'
                        : 'text-white/30'
                    } transition-colors`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-white/60 text-[10px] sm:text-xs mt-1.5 sm:mt-2">
              {feedback.rating > 0 && `${feedback.rating} من 5 نجوم`}
            </p>
          </div>

          {/* Email (optional) */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-yellow mb-1.5 sm:mb-2">
              بريدك الإلكتروني (اختياري)
            </label>
            <input
dir="ltr"              type="email"
              value={feedback.email}
              onChange={(e) => setFeedback({ ...feedback, email: e.target.value })}
              className="w-full px-2.5 py-2 sm:px-3 sm:py-2 bg-blue/20 border-2 border-yellow rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow text-white text-xs sm:text-sm placeholder-white/50"
              placeholder="للتواصل معك إذا احتجنا"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
            <button
              type="button"
              onClick={handleSkip}
              className="flex-1 px-3 py-2 sm:px-4 sm:py-3 bg-blue/20 text-white text-xs sm:text-sm rounded-lg hover:bg-blue/30 transition-colors font-medium border-2 border-white/20"
            >
              تخطي
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-3 py-2 sm:px-4 sm:py-3 bg-yellow text-[#05339c] text-xs sm:text-sm rounded-lg hover:bg-yellow/80 transition-colors font-bold disabled:opacity-50"
            >
              {submitting ? 'جاري الإرسال...' : 'إرسال'}
            </button>
          </div>
        </form>
      </div>

      {/* Instant Response Modal - Appears on top */}
      {showInstantResponse && instantResponse && (
        <div 
          className={`fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-2 sm:p-4 transition-all duration-300 ${
            isAnimatingIn && !isAnimatingOut ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div 
            className={`bg-dark-blue border-2 sm:border-4 border-green-500 rounded-2xl sm:rounded-3xl max-w-2xl w-[96%] sm:w-full shadow-2xl transition-all duration-300 ${
              isAnimatingIn && !isAnimatingOut 
                ? 'scale-100 opacity-100 translate-y-0' 
                : 'scale-90 opacity-0 translate-y-4'
            }`}
          >
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 sm:px-8 sm:py-6 flex justify-between items-center rounded-t-xl sm:rounded-t-2xl">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center transition-all duration-500 delay-100 ${
                  isAnimatingIn && !isAnimatingOut ? 'rotate-0 scale-100' : 'rotate-180 scale-0'
                }`}>
                  <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className={`text-base sm:text-2xl font-bold text-white transition-all duration-500 delay-150 ${
                  isAnimatingIn && !isAnimatingOut ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                }`}>
                  معلومة مهمة جداً!
                </h3>
              </div>
              <button
                onClick={closeInstantResponse}
                className="text-white hover:bg-white/20 p-1.5 sm:p-2 rounded-lg transition-all duration-200 hover:rotate-90"
              >
                <FiX size={20} className="sm:w-7 sm:h-7" />
              </button>
            </div>

            <div className="p-4 sm:p-8">
              <p className={`text-white text-sm sm:text-xl leading-relaxed text-center mb-4 sm:mb-6 font-medium transition-all duration-500 delay-200 ${
                isAnimatingIn && !isAnimatingOut ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}>
                {instantResponse}
              </p>
              
              <div className={`flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center transition-all duration-500 delay-300 ${
                isAnimatingIn && !isAnimatingOut ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}>
                {responseLink && (
                  <a 
                    href={responseLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2.5 sm:px-8 sm:py-4 bg-yellow text-[#05339c] rounded-xl hover:bg-yellow/90 transition-all duration-200 font-bold text-sm sm:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-1 text-center"
                  >
                    {responseLinkText}
                  </a>
                )}
                <button
                  onClick={closeInstantResponse}
                  className="px-4 py-2.5 sm:px-8 sm:py-4 bg-blue/30 text-white rounded-xl hover:bg-blue/40 transition-all duration-200 font-bold text-sm sm:text-lg border-2 border-white/20 hover:border-white/40 hover:scale-105"
                >
                  فهمت، شكراً
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackModal;

